import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";

/**
 * Column manager popover: show/hide, reorder, pin left/right, auto-width, reset.
 * Preferences are lifted to the parent so they can be persisted per collection.
 */
export default function ColumnManagerButton({
  fields,
  prefs,
  onChange,
  onAutoWidth,
  onReset,
}) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  const hidden = prefs.hidden || {};
  const pinnedLeft = prefs.pinnedLeft || [];
  const pinnedRight = prefs.pinnedRight || [];
  const hiddenCount = fields.filter((f) => hidden[f]).length;

  const patch = (next) => onChange({ ...prefs, ...next });

  const toggleHidden = (field) => patch({ hidden: { ...hidden, [field]: !hidden[field] } });

  const move = (field, dir) => {
    const order = [...fields];
    const i = order.indexOf(field);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    patch({ order });
  };

  const togglePin = (field, side) => {
    let left = pinnedLeft.filter((f) => f !== field);
    let right = pinnedRight.filter((f) => f !== field);
    if (side === "left" && !pinnedLeft.includes(field)) left = [...left, field];
    if (side === "right" && !pinnedRight.includes(field)) right = [...right, field];
    patch({ pinnedLeft: left, pinnedRight: right });
  };

  return (
    <>
      <Tooltip title="Manage columns">
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={
            <Badge color="primary" badgeContent={hiddenCount} invisible={!hiddenCount}>
              <ViewColumnIcon fontSize="small" />
            </Badge>
          }
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          Columns
        </Button>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 340, p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Column Manager</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={onAutoWidth}>Auto width</Button>
              <Button size="small" color="warning" onClick={onReset}>Reset</Button>
            </Stack>
          </Stack>
          <Divider />
          <List dense sx={{ maxHeight: 380, overflowY: "auto" }}>
            {fields.map((field, index) => {
              const isLeft = pinnedLeft.includes(field);
              const isRight = pinnedRight.includes(field);
              return (
                <ListItem key={field} disableGutters sx={{ gap: 0.5 }}>
                  <Switch
                    size="small"
                    checked={!hidden[field]}
                    onChange={() => toggleHidden(field)}
                    disabled={field === "id"}
                  />
                  <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {field}
                  </Typography>
                  <Tooltip title="Pin left">
                    <IconButton size="small" color={isLeft ? "primary" : "default"} onClick={() => togglePin(field, isLeft ? null : "left")}>
                      {isLeft ? <PushPinIcon fontSize="inherit" /> : <PushPinOutlinedIcon fontSize="inherit" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Pin right">
                    <IconButton size="small" color={isRight ? "secondary" : "default"} onClick={() => togglePin(field, isRight ? null : "right")}>
                      <PushPinOutlinedIcon fontSize="inherit" sx={{ transform: "scaleX(-1)" }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" disabled={index === 0} onClick={() => move(field, -1)}>
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton size="small" disabled={index === fields.length - 1} onClick={() => move(field, 1)}>
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Popover>
    </>
  );
}
